/**
 * Type definitions and interfaces for the Workflows feature.
 * Contains all TypeScript interfaces, types, and enums used throughout the workflow system.
 *
 * Organization:
 * 1. Core Types & Enums
 * 2. Workflow Schema Types
 * 3. Node Definition Types
 * 4. Component Props Interfaces
 * 5. Form & State Types
 * 6. Utility Types
 */
// ============================================================================
// IMPORTS
// ============================================================================
import type { Dispatch, SetStateAction } from "react"

import type { Edge, Node, OnEdgesChange } from "reactflow"

import type { FormikProps } from "formik"

import type { MenuOptions } from "../../../components/UI/SelectWithSearch"
import type { FilterCondition, MapValueTypeToFeatureType } from "../../../types/custom/rules"
import type { CalculatorList } from "../../../types/generated/api/CalculatorList"
import type { CalculatorVersionRetrieve } from "../../../types/generated/api/CalculatorVersionRetrieve"
import type { ConditionListFile } from "../../../types/generated/api/ConditionListFile"
import type { DeploymentList } from "../../../types/generated/api/DeploymentList"
import type { ModelDeploymentSchemaFeature } from "../../../types/generated/api/ModelDeploymentSchemaFeature"
import type { PaginatedCalculatorListList } from "../../../types/generated/api/PaginatedCalculatorListList"
import type { PaginatedDeploymentListList } from "../../../types/generated/api/PaginatedDeploymentListList"
import type { PaginatedProgramList } from "../../../types/generated/api/PaginatedProgramList"
import type { PaginatedRuleSetListList } from "../../../types/generated/api/PaginatedRuleSetListList"
import type { PaginatedScoreCardSetListList } from "../../../types/generated/api/PaginatedScoreCardSetListList"
import type { PaginatedScriptGroupListList } from "../../../types/generated/api/PaginatedScriptGroupListList"
import type { PaginatedTagListListList } from "../../../types/generated/api/PaginatedTagListListList"
import type { Program } from "../../../types/generated/api/Program"
import type { RuleList } from "../../../types/generated/api/RuleList"
import type { RuleSet } from "../../../types/generated/api/RuleSet"
import type { ScoreCardSetList } from "../../../types/generated/api/ScoreCardSetList"
import type { Script } from "../../../types/generated/api/Script"
import { ScriptGroupList } from "../../../types/generated/api/ScriptGroupList"
import type { TagListList } from "../../../types/generated/api/TagListList"
import type { WorkflowConstantFeature } from "../../../types/generated/api/WorkflowConstantFeature"
import type { WorkflowGroupRetrieve } from "../../../types/generated/api/WorkflowGroupRetrieve"
import { WorkflowInputFeature } from "../../../types/generated/api/WorkflowInputFeature"
import type { Schema } from "../../Predictions/Interfaces"

// ============================================================================
// CORE TYPES & ENUMS
// ============================================================================
// Node type identifiers
export type NodeType =
  | "ScorecardsetNode"
  | "RulesetNode"
  | "ProgramNode"
  | "DecisionNode"
  | "StartNode"
  | "ProjectNode"
  | "ScriptNode"
  | "TaglistNode"
  | "CalculatorNode"
  | "MarketPlaceScriptNode"
  | "LoopStartNode"
  | "LoopEndNode"
  | "FilterNode"

// Core enums
export enum MapNodeTypeToFlowFormatEnum {
  "filter" = "FilterNode",
  "label" = "LabeledNode",
  "ruleset" = "RulesetNode",
  "program" = "ProgramNode",
  "decision" = "DecisionNode",
  "project" = "ProjectNode",
  "scorecardset" = "ScorecardsetNode",
  "script" = "ScriptNode",
  "taglist" = "TaglistNode",
  "calculator" = "CalculatorNode",
  "addBlock" = "AddBlockNode",
  "loop-start" = "LoopStartNode",
  "loop-end" = "LoopEndNode",
}

export enum MapWorkflowValueTypeToFeatureType {
  "string" = WorkflowInputFeature.type.TEXT,
  "date" = WorkflowInputFeature.type.DATE,
  "number" = WorkflowInputFeature.type.NUMBER,
  true = WorkflowInputFeature.type.BOOLEAN,
  false = WorkflowInputFeature.type.BOOLEAN,
  "null" = WorkflowInputFeature.type.UNDEFINED,
}

export enum MappedOperators {
  "<" = "less than",
  ">" = "more than",
  "<=" = "less than/equal",
  ">=" = "more than/equal",
  "=" = "equal",
  "between" = "between",
}

export enum DataKeyName {
  "RulesetNode" = "ruleset",
  "ProjectNode" = "project",
  "DecisionNode" = "ruleset", // DecisionNode uses ruleset as data key
  "ProgramNode" = "program",
  "ScorecardsetNode" = "scorecard_set",
  "StartNode" = "start",
  "TaglistNode" = "taglist",
}

export enum DropdownTitle {
  "RulesetNode" = "choose ruleset",
  "ProgramNode" = "choose program",
  "ProjectNode" = "choose project",
  "ScorecardsetNode" = "choose scorecardset",
  "DecisionNode" = "choose ruleset", // DecisionNode uses ruleset dropdown
  "TaglistNode" = "choose taglist",
}

// ============================================================================
// WORKFLOW SCHEMA TYPES
// ============================================================================

export type SchemaFeature = {
  name: string
  source?: string
  type?: WorkflowInputFeature.type | MapValueTypeToFeatureType
  is_used?: boolean | null
  is_required?: boolean
  file?: boolean
  id?: string
  new?: boolean
  remove?: boolean
}

export type ConstantFeature = {
  id?: string
  name: string
  type?: WorkflowConstantFeature.type
  is_used?: boolean | null
  value: unknown
  new?: boolean
}

export type WorkflowSchema = {
  features: Array<SchemaFeature>
  constants: Array<ConstantFeature>
  workflowName: string
}

export type FeatureData = {
  workflows: Array<{
    name: string
    version: string
  }>
  isRequired?: boolean
  type: WorkflowInputFeature.type
  name: string
  featureCategory: "input" | "computed" | "constant"
}

export type FeatureMapping = {
  node_feature: string
  workflow_feature: string | number
  type?: WorkflowInputFeature.type
}

export type InputFeature = {
  feat: string
  value: string | number
}

export type ComputedFeatures = {
  computed_feature_names: Array<string>
  workflow_name: string
  workflow_uuid: string
}

// ============================================================================
// NODE DEFINITION TYPES
// ============================================================================

// Base node structure
export interface BaseWorkflowNode {
  node_type: string
  resolution_route: number | string
}

// Specific node types
export interface RulesetNode extends BaseWorkflowNode {
  uncovered_route: number | string
  ruleset: string
}

export interface FilterNode extends BaseWorkflowNode {
  feature_nesting_operator: string
  true_route: number | string
  false_route: number | string
  condition: string
}

export interface TaglistNode extends BaseWorkflowNode {
  taglist: string
}

export interface ScorecardsetNode extends BaseWorkflowNode {
  scorecard_set: string
}

export interface ProgramNode extends BaseWorkflowNode {
  prorgram: string
}

export interface CalculatorNode extends BaseWorkflowNode {
  computed_feature_name: string
  equation: string
  feature_nesting_operator: string
}

export interface ScriptNode extends BaseWorkflowNode {
  computed_feature_name: string
  script: string
  feature_mappings?: Array<FeatureMapping>
  scriptType?: "marketplace" | "custom"
}

export interface ProjectNode extends BaseWorkflowNode {
  computed_feature_name: string
  project: string
}

export interface LabelNode extends BaseWorkflowNode {
  description?: string
  return_label: string
}

// Union type for all workflow nodes
export type WorkflowNode =
  | LabelNode
  | FilterNode
  | RulesetNode
  | ScorecardsetNode
  | ProgramNode
  | ScriptNode
  | TaglistNode
  | CalculatorNode
  | ProjectNode

// ============================================================================
// COMPONENT PROPS INTERFACES
// ============================================================================

// Common data item types (consolidated from duplicates)
export type SelectionDataItemType = RuleSet | Program | ScoreCardSetList | DeploymentList | TagListList

// Paginated list types
export type PaginatedListType =
  | PaginatedProgramList
  | PaginatedRuleSetListList
  | PaginatedScoreCardSetListList
  | PaginatedTagListListList
  | PaginatedDeploymentListList

// Current node data types
export type CurrentNodeData =
  | PaginatedRuleSetListList
  | PaginatedProgramList
  | PaginatedScoreCardSetListList
  | PaginatedTagListListList
  | PaginatedDeploymentListList
  | undefined

// Base props for all selection-based nodes
export interface BaseBaseNodeProps {
  id?: string
  menuView?: boolean
  data?: {
    updateGraph?: (data: UpdateGraphParams) => void
    nodeType?: NodeType
    readMode?: boolean
    editMode?: boolean
    isNodeInLoop?: boolean
    isNodeInNestedLoop?: boolean
    parentLoopId?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: any
    schema?: { features: Array<WorkflowInputFeature>; is_valid: boolean }
    feature_mappings?: Array<FeatureMapping>
  }
  setAllNodes?: (nodes: Node[], isNodeBetween: boolean) => void
}

// Generic selection node props type
export type BaseNodeProps<T extends SelectionDataItemType, P extends PaginatedListType> = {
  data?: BaseBaseNodeProps["data"] & {
    [K in keyof T]?: T
  } & {
    [K in keyof P]?: P
  }
} & BaseBaseNodeProps

// Specific node props (using the generic type)
export type TaglistNodeProps = BaseNodeProps<TagListList, PaginatedTagListListList> & {
  data?: BaseBaseNodeProps["data"] & {
    taglist?: TagListList
    taglists?: PaginatedTagListListList
  }
}

export type ScorecardsetNodeProps = BaseNodeProps<ScoreCardSetList, PaginatedScoreCardSetListList> & {
  data?: BaseBaseNodeProps["data"] & {
    scorecard_set?: ScoreCardSetList
    scorecardsets?: PaginatedScoreCardSetListList
  }
}

export type RulesetNodeProps = BaseNodeProps<RuleSet, PaginatedRuleSetListList> & {
  data?: BaseBaseNodeProps["data"] & {
    ruleset?: RuleSet
    rulesets?: PaginatedRuleSetListList
    rulesetName?: string
    rules?: RuleList[]
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: FormikProps<any>
  }
}

export type ProjectNodeProps = BaseNodeProps<DeploymentList, PaginatedDeploymentListList> & {
  data?: BaseBaseNodeProps["data"] & {
    project?: DeploymentList
    projects?: PaginatedDeploymentListList
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: FormikProps<any>
  }
}

export type ProgramNodeProps = BaseNodeProps<Program, PaginatedProgramList> & {
  data?: BaseBaseNodeProps["data"] & {
    program?: Program
    programs?: PaginatedProgramList
  }
}

// Loop node props
export interface LoopStartNodeProps {
  id?: string
  menuView?: boolean
  data?: {
    updateGraph: (data: UpdateGraphParams) => void
    readMode: boolean
    strategy: "Parallel" | "Combined" | "PARALLEL" | "COMBINED"
    loopName: string
    loopId: string
    parentLoopName: string
    parentLoopId: string
    parentId: string
    schema: { features: Array<WorkflowInputFeature>; is_valid: boolean }
    input_lists: Array<string>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: FormikProps<any>
  }
}

export type Features = {
  [key: string]: string
}

export interface LoopEndNodeProps {
  id?: string
  menuView?: boolean
  data?: {
    readMode: boolean
    parentLoopName: string
    parentLoopId: string
    parentId: string
  }
}

// Sort edge position type for LoopEndNode
export type SortEdgePosition = {
  edge: Edge
  node: Node
}

// ============================================================================
// FORM & STATE TYPES
// ============================================================================

export interface WorkflowSimulationFormik {
  workflowID: string
  source: string
  totalRequestsNumber: number
  shouldProceed: boolean
  predictions_datafile?: string
  predictions_start_time?: string
  predictions_end_time?: string
  feedback_column?: string | null
}

export type ConfirmCloseDialogState = {
  isOpen: boolean
  action?: { name: string; workflowName?: string; workflowId?: string }
}

export type UpdateGraphParams = {
  dataAfterDeletion?: { nodes: Node[]; edges: Edge[] }
  arrayOfNewNodes?: Node[]
  isNodeBetween?: boolean
  targetNode?: string
  sourceNode?: string
  edgeLabel?: string
  areBranchesSwapped?: { filterNodeId: string }
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

export interface ExtendedNodeData extends Node {
  uuid?: string
  node_type?: string
  data: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any
  }
}

export type BaseNodeData = {
  readMode: boolean
  parentId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

export type NodeSimpleChipProps = {
  data: {
    nodeType?: NodeType
    readMode?: true
    ruleset?: RuleSet
    project?: DeploymentList
    program?: Program
    calculator: CalculatorList
    taglist?: TagListList
    scorecard_set?: ScoreCardSetList
    rulesetName?: string
    scriptName?: string
    isNodeInLoop?: boolean
    editMode?: boolean
    schema?: Schema
    return_label?: { name: string }
    isSelected: boolean
    selectedId: string
  }
  menuView?: boolean
  id?: string
  setAllNodes?: (nodes: Node[], isNodeBetween: boolean) => void
}

export interface NodeConfig {
  nodeTitle: string
  classname: string
  textColorClassname: string
  nodeName?: string
}

export type WorkflowCanvasContextType = {
  mode: "view" | "create" | "edit"
  isWorkflowLoading: boolean
  isWorkflowFetching: boolean
  isWorkflowsLoading: boolean
  workflow: WorkflowGroupRetrieve | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: any
  onEdgesChange: OnEdgesChange
}

export type WorkflowSchemaDrawerProps = {
  open: boolean
  onClose: () => void
  formik: FormikProps<WorkflowSchema>
  schema?: WorkflowInputFeature[]
  workflowName: string
  isWorkflowsLoading: boolean
  isWorkflowInReadMode: boolean
  isWorkflowDeprecated: boolean
}

export type ConflictType = {
  [key: string]: {
    oldType?: WorkflowInputFeature.type | MapValueTypeToFeatureType
    newType: WorkflowInputFeature.type
  }
}

export type ConflictsListType = {
  unUsedFeatures: ConflictType
  inUseFeatures: ConflictType
}

export type WorkflowSchemaContextType = {
  setStep: Dispatch<SetStateAction<number>>
  setIsNextStepEnabled: Dispatch<SetStateAction<boolean>>
  acceptedFile: File[]
  setAcceptedFile: Dispatch<SetStateAction<File[]>>
  conflictsList: ConflictsListType
  setConflictsList: Dispatch<SetStateAction<ConflictsListType>>
}

export type InputFeaturesProps = {
  formik: FormikProps<WorkflowSchema>
  schema?: WorkflowInputFeature[]
  isWorkflowsLoading: boolean
  currentMode: "create" | "read" | "edit"
  rowsPerPage: number
  setRowsPerPage: Dispatch<SetStateAction<number>>
}

export type ConstantsProps = {
  formik: FormikProps<WorkflowSchema>
  isWorkflowsLoading: boolean
  currentMode: "create" | "edit" | "read"
  isJsonMode: boolean
  setIsJsonMode: Dispatch<SetStateAction<boolean>>
  jsonText: string
  setJsonText: Dispatch<SetStateAction<string>>
  rowsPerPage: number
  setRowsPerPage: Dispatch<SetStateAction<number>>
}

export type ConflictsListProps = {
  formik: FormikProps<WorkflowSchema>
  features: {
    [key: string]: SchemaFeature
  }
}

export type ConflictCardProps = {
  isUsed: boolean
  oldFeatureData: {
    name: string
    type: WorkflowInputFeature.type
  }
  NewFeatureData: {
    name: string
    type: WorkflowInputFeature.type
  }
  handleOverwrite?: () => void
  handleIgnore?: () => void
}

// Props for the shared SelectDataMenu component
export interface SelectDataMenuProps {
  nodeId: string | null
  dataType: NodeType
  dataItemId:
    | string
    | TagListList
    | RuleSet
    | DeploymentList
    | Program
    | ScoreCardSetList
    | CalculatorDataItemType
    | ScriptGroupList
    | undefined
  menuView: boolean | undefined
  itemData:
    | PaginatedDeploymentListList
    | PaginatedListType
    | PaginatedCalculatorListList
    | PaginatedScriptGroupListList
    | undefined
  selectedValue: string
  setSelectedValue: Dispatch<
    SetStateAction<string | undefined | SelectionDataItemType | CalculatorDataItemType | ScriptGroupList>
  >
  isDataLoading: boolean
}

// Node data based on type
export interface NodeDataBasedOnType {
  nodeLogo: React.ReactElement
  nodeTitle: string
  nodeBody?: string
  logoClassName?: string
  onRightIconClick?: () => void
}

// ============================================================================
// SCRIPT NODE INTERFACES
// ============================================================================

// Props for the SelectDataMenu component in ScriptNode
export interface ScriptSelectionDropDownProps {
  /** Unique identifier for the node */
  nodeId: string | null
  /** Type of selection data */
  dataType: NodeType
  /** Currently selected data item */
  dataItemId: string | Script | undefined
  /** Whether the menu is in view mode */
  menuView: boolean | undefined
  /** Data items available for selection */
  itemData: PaginatedDeploymentListList | PaginatedScriptGroupListList | PaginatedScriptGroupListList | undefined
  /** Currently selected value */
  selectedValue: string
  /** Function to update selected value */
  setSelectedValue: Dispatch<SetStateAction<string | undefined | Script>>
  /** Function to add computed feature */
  addComputedFeature: (feature: string) => void
  /** Whether data is currently loading */
  isDataLoading: boolean
  /** Function to update selected template value */
  setSelectedTemplateValue: Dispatch<SetStateAction<string | undefined | null>>
  /** Translation function */
  t: (key: string) => string
}

// Script menu options interface
export interface ScriptMenuOptions extends Omit<MenuOptions, "value"> {
  value: { script_uuid: string; template_uuid?: string }
}

// Props for the ScriptNode component
export interface ScriptNodeProps {
  /** Node data containing configuration and state */
  data: {
    updateGraph?: (data: UpdateGraphParams) => void
    nodeType?: NodeType
    readMode?: true
    script?: Script
    scriptName?: string
    file?: string
    isNodeInLoop?: boolean
    scripts?: PaginatedScriptGroupListList
    feature_mappings?: Array<FeatureMapping>
    editMode?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: FormikProps<any>
    scriptType?: "marketplace" | "custom"
  }
  /** Whether the node is in menu view mode */
  menuView?: boolean
  /** Unique identifier for the node */
  id?: string
  /** Function to update all nodes */
  setAllNodes?: (nodes: Node[], isNodeBetween: boolean) => void
}

// Formik values for feature mapping
export interface FeatureMappingFormikValues {
  /** Array of feature mappings */
  feature_mappings: FeatureMapping[]
  /** Retrieved node data */
  retrievedNode: { uuid: string; feature_mappings: FeatureMapping[] }
  /** Cached nodes data */
  cachedNodes: { [key: string]: FeatureMapping[] }
  /** Live model schema */
  liveModelSchema: ModelDeploymentSchemaFeature[]
}

// ============================================================================
// CALCULATOR NODE INTERFACES
// ============================================================================

// Calculator node specific selection dropdown props
export interface CalculatorSelectionDropDownProps {
  nodeId: string | null
  dataType: NodeType
  dataItemId: (Omit<CalculatorVersionRetrieve, "is_active"> & { name: string; uuid: string }) | string | undefined
  menuView: boolean | undefined
  itemData: PaginatedCalculatorListList | undefined
  selectedValue: string
  setSelectedValue: Dispatch<SetStateAction<string | undefined | CalculatorDataItemType>>
  isDataLoading: boolean
}

// Calculator paginated list type
export type CalculatorPaginatedListType = PaginatedCalculatorListList

// Calculator data item type
export type CalculatorDataItemType = Omit<CalculatorVersionRetrieve, "is_active"> & { name: string; uuid: string }

// Calculator node specific props
export interface CalculatorNodeProps {
  data: {
    updateGraph?: (data: UpdateGraphParams) => void
    nodeType?: NodeType
    readMode?: boolean
    calculator?: Omit<CalculatorVersionRetrieve, "is_active"> & { name: string; uuid: string }
    calculators?: PaginatedCalculatorListList
    isNodeInLoop?: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik?: FormikProps<any>
  }
  menuView?: boolean
  id?: string
  setAllNodes?: (nodes: Node[], isNodeBetween: boolean) => void
}

// ============================================================================
// CONDITION COMPONENT INTERFACES
// ============================================================================

// Props for the Condition component
export interface ConditionProps {
  isReadMode: boolean
  isEditMode: boolean
  condition?: string
  formik: FormikProps<FilterFormikValues>
  id: string
  nodeId: string
  isMenuView: boolean
  index: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any
  handleRemoveRule: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allFeatures: Array<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  workflowFormik: any
}

// ============================================================================
// FILTER NODE INTERFACES
// ============================================================================

// Props for filter node
export interface FilterNodeProps {
  id?: string
  menuView?: boolean
  data?: {
    condition: string
    condition_list_files: ConditionListFile[] | null
    fileData: ConditionListFile
    readMode: boolean
    editMode: boolean
    isNodeInLoop: boolean
    isNodeInNestedLoop: boolean
    parentLoopId: string
    feature: string
    operator: string
    type: string
    value: string
    filesCount: number
    secondValue: string
    secondFeature: string
    valueOrFeature: "Value" | "Feature"
    updateGraph: (data: UpdateGraphParams) => void
    rules: Array<FilterCondition>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    schema: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formik: FormikProps<any>
  }
}

// Selected branch to delete type
export type SelectedBranchToDelete = "True branch" | "False branch"

// Deletion case type
export type DeletionCase =
  | "DeleteNodeAndAllChildrenNodes"
  | "DeleteEmptyNodeInsideLoop"
  | "DeleteSelectedBranch"
  | "DeleteNodeAndKeepConnectedBranch"

// Filter branch deletion options
export type FilterBranchDeletionOptions = "Filter only" | "True branch" | "False branch" | "All nodes"

// Filter formik values
export interface FilterFormikValues {
  rules: Array<FilterCondition>
  filesCount: number
  fileData: ConditionListFile
  condition_list_files?: ConditionListFile[] | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parentLoopsFeatureList: Array<any>
}
